import Joi from "joi";
import { useState, useEffect } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import {
  getColleges,
  getUserTypes,
  getUserPageBlocks,
  getCollegeDepartments,
} from "../../../../../action/common/commonActionCreator";
import { createTechnologyResource } from "../../../../../action/masters/technologyResourceActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  ConfigUserTypes,
  UserTypes,
} from "../../../../../models/dashboard/adminDashboard";
import {
  ICreateTechnologyResource,
  ITechnologyResourceData,
} from "../../../../../models/master/technologyResource";
import LoaderSpinner from "../../../../../common-components/loader";
import { masterType } from "../../../../../constants/masterTypes";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { setPageBlocks } from "../../../../../store/slices/common/commonSlice";
import {
  maxImageSize,
  urlRegex,
} from "../../../../../constants/commonConstants";

const CreateTechnologyResource = (props: ICreateTechnologyResource) => {
  const {
    createTechnologyResource,
    setShowCreate,
    getColleges,
    colleges,
    userTypes,
    getUserTypes,
    pageBlocks,
    getUserPageBlocks,
    getAllTechnologyResources,
    getCollegeDepartments,
    collegeDepartments,
  } = props;

  const initialData: ITechnologyResourceData = {
    id: null,
    resourceName: "",
    file: null,
    userType: UserTypes.Staff,
    description: "",
    url: "",
    sso: "",
    isActive: true,
    priority: 0,
    collegeIds: [],
    pageBlockIds: [],
    departmentIds: [],
  };

  // states
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "userType") {
      getUserPageBlocks(
        Number(event.target.value),
        masterType.technologyResource
      );
    }
    if (event.target.name === "collegeIds") {
      getCollegeDepartments(_data.collegeIds.toString());
    }
    if (pageBlocks && pageBlocks.length > 0) {
      _data.pageBlockIds = [pageBlocks[0].id];
    }
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/ico",
        "image/svg+xml",
        "image/x-icon",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an icon.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  const schema = Joi.object().keys({
    resourceName: Joi.string().required().label("Resource Name is required"),
    userType: Joi.number().greater(0).required().label("Select User Type"),
    url: Joi.string().required().regex(urlRegex).label("Enter Valid URL"),
    file: Joi.object().required().label("Icon is required"),
    description: Joi.string().required().label("Description is required"),
    collegeIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select University Management"),
    departmentIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select Department"),
    sso: Joi.string().when("isSSOEnabled", {
      is: true,
      then: Joi.string().required().regex(urlRegex).label("Enter Valid SSO"),
      otherwise: Joi.string().allow("", null).optional(),
    }),
  });

  const validationData = {
    ...data,
    // isSSOEnabled: isSSOEnabled,
  };

  const onSave = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("resourceName", data.resourceName);
      formData.append("userType", data.userType?.toString() || "");
      formData.append("file", data.file);
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("url", data.url);
      formData.append("isActive", data.isActive.toString());
      formData.append("priority", data.priority.toString());
      data.collegeIds.map((item, index) => {
        formData.append(`collegeIds[${index}]`, item.toString());
      });
      data.pageBlockIds.map((item, index) => {
        formData.append(`pageBlockIds[${index}]`, item.toString());
      });
      data.departmentIds.map((item, index) => {
        formData.append(`departmentIds[${index}]`, item.toString());
      });
      setLoading(true);
      setIsDisabled(true);
      try {
        await createTechnologyResource(formData, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  // setting sweet alert dynamically
  const setAlert = (type: string, name?: string) => {
    let alertData = getAlertData(type, name);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllTechnologyResources();
      setShowCreate(false);
    }
  };

  //setting warning alert on cancel
  const onCancel = async () => {
    setShowAlertCancel(true);
    setAlert(alertConstants.cancelCheck);
  };

  useEffect(() => {
    getColleges();
    getUserTypes();
    getUserPageBlocks(ConfigUserTypes.Staff, masterType.technologyResource);
    return () => {
      dispatch(setPageBlocks([]));
    };
  }, []);

  useEffect(() => {
    if (colleges && colleges.length > 0) {
      setData((prevData) => ({
        ...prevData,
        collegeIds: colleges.map((college) => college.id),
      }));
    }
  }, [colleges]);

  useEffect(() => {
    if (data.collegeIds?.length > 0) {
      getCollegeDepartments(data.collegeIds.toString());
    }
  }, [data.collegeIds]);

  useEffect(() => {
    if (
      collegeDepartments &&
      data.collegeIds &&
      collegeDepartments.length > 0 &&
      data.collegeIds.length > 0
    ) {
      setData((prevData) => ({
        ...prevData,
        departmentIds: collegeDepartments?.map((dept) => dept.id),
      }));
    }
  }, [collegeDepartments, data.collegeIds]);

  return (
    <>
      <div className="modal-header">
        <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
          Add New Technology Resource
        </h1>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={() => setShowCreate(false)}
        ></button>
      </div>
      <div className="modal-body">
        <div className="row">
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"User Type "}
              onChange={onChange}
              isMandatory={true}
              value={data.userType}
              name={"userType"}
              disable
              optionsList={sortAlphabetically(userTypes)}
            />
            {errors?.userType && (
              <div className="text-danger">{errors.userType}</div>
            )}
          </div>

          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Resource Name "}
              onChange={onChange}
              isMandatory={true}
              value={data.resourceName}
              name={"resourceName"}
              maxLength={50}
            />
            {errors?.resourceName && (
              <div className="text-danger">{errors.resourceName}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <div className="d-flex">
              <label className="profile_details_text">
                Icon<i className="label-asterisk"> * </i>
              </label>
              <p className="ms-2 label-info">
                <i className="fa-solid fa-circle-info"></i>
                <span>
                  Upload an icon (SVG, PNG, or ICO). For best results, use a
                  square shape with a transparent background.
                </span>
              </p>
            </div>
            <div className="inputfile-flex">
              {imagePreview && (
                <div className="inputfile-img">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: "50px", height: "50px" }}
                  />
                </div>
              )}
              <div className="inputfile-wrap">
                <input
                  type="file"
                  name="file"
                  id="image"
                  className="form-control d-none"
                  required
                  onChange={onFileChange}
                  accept="image/png, image/svg+xml, image/x-icon, image/ico, .svg, .png, .ico,"
                  value={errors?.file && ""}
                />
                <div className="inputfile">
                  <label htmlFor="image">Choose File</label>
                  <span>
                    {imageName
                      ? imageName.trim().split("/").pop()
                      : "No File Choosen"}
                  </span>
                </div>
              </div>
            </div>
            {errors?.file && <div className="text-danger">{errors.file}</div>}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Page-Block "}
              onChange={onChange}
              isMandatory={false}
              value={pageBlocks?.length > 0 ? pageBlocks[0].name : ""}
              name={"pageBlockIds"}
              disable
            />
          </div>
          <div className="col-md-12 form-group">
            <label className="profile_details_text">
              Description<i className="label-asterisk"> * </i>
            </label>
            <textarea
              name="description"
              value={data.description}
              onChange={onChange}
              maxLength={1000}
            ></textarea>
            {errors?.description && (
              <div className="text-danger">{errors.description}</div>
            )}
          </div>

          <div className="col-md-12 form-group">
            <FloatingLabelTextInput
              label={"URL "}
              onChange={onChange}
              isMandatory={true}
              value={data.url}
              name={"url"}
              maxLength={500}
            />
            {errors?.url && <div className="text-danger">{errors.url}</div>}
          </div>
          {/* <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"Is SSO Enabled "}
              onChange={(e: any) => setIsSSOEnabled(e.target.value)}
              isMandatory={true}
              value={isSSOEnabled}
              name={"isSSOEnabled"}
              optionsList={ssoOptionsList}
            />
          </div>
          {isSSOEnabled && (
            <div className="col-md-6 form-group">
              <FloatingLabelTextInput
                label={"SSO"}
                onChange={onChange}
                isMandatory={true}
                value={data.sso}
                name={"sso"}
                maxLength={500}
              />
              {errors?.sso && <div className="text-danger">{errors.sso}</div>}
            </div>
          )} */}
          <div className={"col-md-12 form-group"}>
            <MultipleSelect
              label={"University Management"}
              onChange={onChange}
              isMandatory={true}
              values={data.collegeIds as number[]}
              name={"collegeIds"}
              optionList={sortAlphabetically(colleges)}
            />
            {errors?.collegeIds && (
              <div className="text-danger">{errors.collegeIds}</div>
            )}
          </div>
          <div className="col-md-12 form-group">
            <MultipleSelect
              label={"Department"}
              onChange={onChange}
              isMandatory={true}
              values={data.departmentIds as number[]}
              name={"departmentIds"}
              optionList={sortAlphabetically(collegeDepartments)}
            />
            {errors?.departmentIds && (
              <div className="text-danger">{errors.departmentIds}</div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={isDisabled}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            data-bs-dismiss="modal"
            onClick={() => setShowCreate(false)}
            disabled={isDisabled}
          >
            Close
          </button>
        </div>
      </div>
      <SweetAlert
        show={showAlert}
        success={false}
        type={alertType}
        title={alertTitle}
        onConfirm={() => onConfirm()}
        showCancel={showAlertCancel}
        onCancel={() => {
          setShowAlert(false);
          setShowAlertCancel(false);
        }}
        cancelBtnText={"No"}
        confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
      ></SweetAlert>
      {loading && <LoaderSpinner />}
    </>
  );
};

const mapDispatchToProps = {
  createTechnologyResource: (data: any, callBack: Function) =>
    createTechnologyResource(data, callBack),
  getColleges: () => getColleges(),
  getUserTypes: () => getUserTypes(),
  getUserPageBlocks: (userType: number, masterType: string) =>
    getUserPageBlocks(userType, masterType),
  getCollegeDepartments: (collegeIds: string) =>
    getCollegeDepartments(collegeIds),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    pageBlocks: state.common.pageBlocks,
    userTypes: state.common.userTypes,
    collegeDepartments: state.common.collegeDepartments,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateTechnologyResource);
